import { QueryClientProvider } from '@tanstack/react-query'
import { createRoot, type Root } from 'react-dom/client'
import { queryClient } from '../state/query-client'
import { ComponentBoundary } from './boundary'
import { emit } from './events'
import { PortalHostProvider } from './portal'
import { type AnyProps, resolve } from './registry'
import { report } from './report'
import { applyStyles } from './styles'

export interface MountHandle {
  name: string
  element: Element
  unmount(): void
}

interface MountRecord {
  name: string
  root: Root
  container: HTMLElement
  portalHost: HTMLElement
}

/**
 * Strong references, deliberately. We need to enumerate mounts for
 * `unmountAll()` and for pruning nodes Webflow removes from the DOM, neither
 * of which a WeakMap supports. `pruneDetached()` keeps this from growing.
 */
const records = new Map<Element, MountRecord>()

/** Names we've already warned about, so a missing component logs once, not once per element. */
const warnedMissing = new Set<string>()

/**
 * Render a registered component into an element, inside its own shadow root.
 *
 * Idempotent: mounting an already-mounted element returns the existing handle
 * rather than double-rendering. This matters because the embed script can be
 * evaluated more than once on a Webflow page, and because our MutationObserver
 * re-scans on DOM changes.
 */
export function mountInto(
  element: Element,
  name: string,
  props: AnyProps = {},
): MountHandle | null {
  const existing = records.get(element)
  if (existing) return toHandle(element, existing)

  const Component = resolve(name)
  if (!Component) {
    if (!warnedMissing.has(name)) {
      warnedMissing.add(name)
      console.warn(
        `[codeverse] no component registered as "${name}" — leaving the element alone in case it registers later`,
      )
    }
    emit('component:missing', { name, element })
    return null
  }

  const shadow = attachShadow(element, name)
  if (!shadow) return null

  applyStyles(shadow)

  const doc = element.ownerDocument
  const container = doc.createElement('div')
  container.setAttribute('data-cv-root', name)

  const portalHost = doc.createElement('div')
  portalHost.setAttribute('data-cv-portal', '')

  shadow.append(container, portalHost)

  // Each hook replaces React's default, which logs or rethrows onto window and
  // so loses which widget it came from. ComponentBoundary already logs the
  // errors it catches, so onCaughtError only has to report them.
  const root = createRoot(container, {
    onUncaughtError: (error, { componentStack }) => {
      console.error(`[codeverse] uncaught error in "${name}"`, error)
      report(error, { component: name, componentStack })
    },
    onCaughtError: (error, { componentStack }) => {
      report(error, { component: name, componentStack })
    },
    onRecoverableError: (error, { componentStack }) => {
      console.warn(`[codeverse] recovered from an error in "${name}"`, error)
      report(error, { component: name, componentStack, level: 'warning' })
    },
  })

  root.render(
    <ComponentBoundary name={name}>
      <QueryClientProvider client={queryClient}>
        <PortalHostProvider value={portalHost}>
          <Component {...props} />
        </PortalHostProvider>
      </QueryClientProvider>
    </ComponentBoundary>,
  )

  const record: MountRecord = { name, root, container, portalHost }
  records.set(element, record)
  emit('component:mounted', { name, element })

  return toHandle(element, record)
}

/**
 * Elements like `<img>` and `<input>` cannot host a shadow root, and an element
 * with a closed shadow root will throw on a second attach. Either is a markup
 * mistake worth surfacing clearly rather than failing silently.
 */
function attachShadow(element: Element, name: string): ShadowRoot | null {
  if (element.shadowRoot) return element.shadowRoot

  try {
    return element.attachShadow({ mode: 'open' })
  } catch (error) {
    console.error(
      `[codeverse] cannot mount "${name}" into <${element.tagName.toLowerCase()}> — ` +
        'this element type cannot host a shadow root. Use a <div>.',
      error,
    )
    return null
  }
}

function toHandle(element: Element, record: MountRecord): MountHandle {
  return {
    name: record.name,
    element,
    unmount: () => unmountFrom(element),
  }
}

/** Tear down the mount on an element, leaving the element itself in place. */
export function unmountFrom(element: Element): boolean {
  const record = records.get(element)
  if (!record) return false

  records.delete(element)
  record.root.unmount()
  record.container.remove()
  record.portalHost.remove()
  emit('component:unmounted', { name: record.name, element })
  return true
}

/** Tear down every mount on the page. */
export function unmountAll(): void {
  for (const element of [...records.keys()]) unmountFrom(element)
}

/**
 * Unmount anything whose host element has left the document. Webflow's
 * interactions and page transitions remove and re-insert DOM freely, so
 * without this the records map would retain detached nodes and their React
 * roots indefinitely.
 */
export function pruneDetached(): number {
  let pruned = 0
  for (const element of [...records.keys()]) {
    if (!element.isConnected) {
      unmountFrom(element)
      pruned++
    }
  }
  return pruned
}

export function isMounted(element: Element): boolean {
  return records.has(element)
}

export function mountedCount(): number {
  return records.size
}

/** Test helper — unmounts everything and clears warning state. */
export function resetMounts(): void {
  unmountAll()
  warnedMissing.clear()
}
